<template>
  <div class="sleep-sidebar-editor">
    <Sidebar />
    <div class="editor-container">
      <div class="editor-header">
        <button class="back-button" @click="$emit('back')">
          <i class="icon-arrow-left"></i> Back
        </button>
        <h2>{{ editingId ? 'Edit Content' : 'Create New Content' }}</h2>
      </div>
      
      <div class="editor-content">
        <form @submit.prevent="handleSubmit" class="editor-form">
          <!-- Type selection -->
          <div class="form-group">
            <label for="content-type">Content Type</label>
            <select id="content-type" v-model="formData.type" required>
              <option value="" disabled>Select content type</option>
              <option value="bedtime">Bedtime Story</option>
              <option value="breathing">Breathing Exercise</option>
              <option value="sleep_frequency">Sleep Frequency</option>
              <option value="souds_of_nature">Sounds of Nature</option>
              <option value="brain_programming">Brain Programming</option>
            </select>
          </div>
          
          <!-- Title -->
          <div class="form-group">
            <label for="content-title">Title</label>
            <input id="content-title" type="text" v-model="formData.title" required placeholder="Enter content title">
          </div>
          
          <!-- File upload -->
          <div v-if="['breathing', 'bedtime', 'souds_of_nature'].includes(formData.type)" class="form-group">
            <label>Audio File</label>
            <div class="file-upload-container">
              <input type="file" ref="fileInput" @change="handleFileChange" accept="audio/*" class="file-input">
              <button type="button" class="file-select-button" @click="triggerFileInput">
                <i class="icon-upload"></i> Select Audio File
              </button>
              <span v-if="selectedFile" class="selected-file">{{ selectedFile.name }}</span>
              <div v-if="uploadStatus" class="upload-status" :class="{ 'uploading': isUploading }">
                <div class="upload-progress" :style="{ width: uploadProgress + '%' }"></div>
                <span>{{ uploadStatus }}</span>
              </div>
            </div>
          </div>
          
          <!-- Image upload for thumbnail -->
          <div class="form-group">
            <label>Thumbnail Image</label>
            <div class="file-upload-container">
              <input type="file" ref="imageInput" @change="handleImageChange" accept="image/*" class="file-input">
              <button type="button" class="file-select-button" @click="triggerImageInput">
                <i class="icon-image"></i> Select Thumbnail
              </button>
              <span v-if="selectedImage" class="selected-file">{{ selectedImage.name }}</span>
              <div v-if="imageUploadStatus" class="upload-status" :class="{ 'uploading': isImageUploading }">
                <div class="upload-progress" :style="{ width: imageUploadProgress + '%' }"></div>
                <span>{{ imageUploadStatus }}</span>
              </div>
            </div>
          </div>
          
          <!-- Frequency selection for Sleep Frequency -->
          <div v-if="formData.type === 'sleep_frequency'" class="form-group">
            <label for="frequency">Frequency (Hz)</label>
            <input id="frequency" type="number" v-model.number="formData.frequency" step="0.1" min="0.1" max="40" required>
            <small>Enter a value between 0.1 and 40 Hz</small>
          </div>
          
          <!-- Details/Description -->
          <div class="form-group">
            <label for="details">Description</label>
            <textarea id="details" v-model="formData.details" rows="5" placeholder="Enter content description"></textarea>
          </div>

          <!-- Status toggle -->
          <div class="form-group status-toggle">
            <label>Status</label>
            <div class="toggle-container">
              <label class="toggle">
                <input type="checkbox" v-model="formData.status">
                <span class="slider"></span>
              </label>
              <span class="status-label">{{ formData.status ? 'Active' : 'Inactive' }}</span>
            </div>
          </div>
          
          <!-- Error display -->
          <div v-if="error" class="form-error">
            <i class="icon-alert"></i>
            {{ error }}
          </div>
          
          <!-- Cover Image -->
          <div class="image-upload-group">
            <label class="form-label">Cover Image</label>
            <FileUploader
              :api-url="apiUrl"
              :title="'Upload Cover Image'"
              :show-title="true"
              :multiple="false"
              :accept="'image/*'"
              :show-files-list="true"
              :auto-upload="true"
              :image-preview-mode="false"
              :allow-copy="true"
              :support-text="'Upload cover image - URL will be automatically filled below'"
              @files-uploaded="handleCoverImageUploaded"
            />
            
            <div class="form-group mt-2">
              <input 
                v-model="formData.cover_photo" 
                type="url" 
                placeholder="Cover image URL"
                class="form-input"
                :class="{ 'error': errors.cover_photo }"
              />
              <span v-if="errors.cover_photo" class="error-text">{{ errors.cover_photo }}</span>
            </div>
            
            <div v-if="formData.cover_photo" class="image-preview mt-2">
              <CloudflareImagePreview 
                :src="formData.cover_photo" 
                :alt="formData.title || 'Cover Preview'" 
                :fit="'contain'"
                :height="200"
              />
            </div>
          </div>
          
          <!-- Video section for breathing and other video types -->
          <div v-if="formData.type === 'breathing'" class="media-upload-group mt-4">
            <label class="form-label">Video File</label>
            <FileUploader
              :api-url="apiUrl"
              :title="'Upload Video File'"
              :show-title="true"
              :multiple="false"
              :accept="'video/*'"
              :show-files-list="true"
              :auto-upload="true"
              :image-preview-mode="false"
              :allow-copy="true"
              :support-text="'Upload video file - URL will be automatically filled below'"
              @files-uploaded="handleVideoUploaded"
            />
            
            <div class="form-group mt-2">
              <input 
                v-model="formData.video_over" 
                type="url" 
                placeholder="Video URL"
                class="form-input"
              />
            </div>
            
            <div v-if="formData.video_over" class="video-preview mt-2">
              <CloudflareVideoPreview 
                :src="formData.video_over" 
                :poster="formData.cover_photo" 
                :height="220"
                :muted="true"
                :autoplay="false"
                :showControls="true"
              />
            </div>
          </div>
          
          <!-- Form actions -->
          <div class="form-actions">
            <button type="button" class="btn-secondary" @click="$emit('back')">Cancel</button>
            <button type="submit" class="btn-primary" :disabled="isSubmitting">
              <span v-if="isSubmitting" class="spinner small"></span>
              {{ editingId ? 'Update Content' : 'Create Content' }}
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, reactive, onMounted, watch } from 'vue';
import Sidebar from '@/components/Sidebar.vue';
import { CloudflareImagePreview, CloudflareVideoPreview } from '@/components/media';

export default {
  name: 'SleepSidebarEditor',
  components: {
    Sidebar,
    CloudflareImagePreview,
    CloudflareVideoPreview
  },
  props: {
    editingId: {
      type: [Number, String],
      default: null
    },
    initialData: {
      type: Object,
      default: () => ({})
    },
    apiUrl: {
      type: String,
      required: true
    }
  },
  emits: ['save', 'back', 'update:error'],
  setup(props, { emit }) {
    const formData = reactive({
      type: '',
      title: '',
      details: '',
      file_id: null,
      image_id: null,
      frequency: 4.0,
      status: true,
      duration: 1 // Default set to 1 as required
    });
    
    const error = ref(null);
    const isSubmitting = ref(false);
    
    // File upload refs
    const fileInput = ref(null);
    const selectedFile = ref(null);
    const uploadStatus = ref('');
    const uploadProgress = ref(0);
    const isUploading = ref(false);
    const audioFileId = ref(null);
    
    // Image upload refs
    const imageInput = ref(null);
    const selectedImage = ref(null);
    const imageUploadStatus = ref('');
    const imageUploadProgress = ref(0);
    const isImageUploading = ref(false);
    const imageFileId = ref(null);
    
    // Populate form with initial data if editing
    onMounted(() => {
      if (props.editingId && props.initialData) {
        Object.keys(formData).forEach(key => {
          if (props.initialData[key] !== undefined) {
            formData[key] = props.initialData[key];
          }
        });
        
        // Set file IDs
        if (props.initialData.file_id) {
          audioFileId.value = props.initialData.file_id;
          uploadStatus.value = 'File already uploaded';
        }
        
        if (props.initialData.image_id) {
          imageFileId.value = props.initialData.image_id;
          imageUploadStatus.value = 'Image already uploaded';
        }
      }
    });
    
    // Watch for error changes and emit to parent
    watch(error, (newValue) => {
      emit('update:error', newValue);
    });
    
    // File input methods
    const triggerFileInput = () => {
      fileInput.value.click();
    };
    
    const handleFileChange = (event) => {
      const file = event.target.files[0];
      if (file) {
        selectedFile.value = file;
        uploadFile(file, 'audio');
      }
    };
    
    // Image input methods
    const triggerImageInput = () => {
      imageInput.value.click();
    };
    
    const handleImageChange = (event) => {
      const file = event.target.files[0];
      if (file) {
        selectedImage.value = file;
        uploadFile(file, 'image');
      }
    };
    
    // File upload handling
    const uploadFile = async (file, fileType) => {
      const isImage = fileType === 'image';
      
      if (isImage) {
        imageUploadStatus.value = 'Uploading...';
        isImageUploading.value = true;
        imageUploadProgress.value = 0;
      } else {
        uploadStatus.value = 'Uploading...';
        isUploading.value = true;
        uploadProgress.value = 0;
      }
      
      const formData = new FormData();
      formData.append('file', file);
      
      try {
        // Get auth token
        const token = localStorage.getItem('token');
        if (!token) {
          throw new Error('Authentication required');
        }
        
        // Upload the file
        const xhr = new XMLHttpRequest();
        xhr.open('POST', props.apiUrl, true);
        xhr.setRequestHeader('Authorization', `Bearer ${token}`);
        
        // Progress tracking
        xhr.upload.onprogress = (e) => {
          if (e.lengthComputable) {
            const percentComplete = Math.round((e.loaded / e.total) * 100);
            if (isImage) {
              imageUploadProgress.value = percentComplete;
            } else {
              uploadProgress.value = percentComplete;
            }
          }
        };
        
        xhr.onload = function() {
          if (xhr.status === 200) {
            const response = JSON.parse(xhr.responseText);
            if (response.data && response.data.id) {
              if (isImage) {
                imageFileId.value = response.data.id;
                imageUploadStatus.value = 'Upload complete';
                isImageUploading.value = false;
              } else {
                audioFileId.value = response.data.id;
                uploadStatus.value = 'Upload complete';
                isUploading.value = false;
              }
            } else {
              throw new Error('Invalid response from server');
            }
          } else {
            throw new Error(`Upload failed: ${xhr.status}`);
          }
        };
        
        xhr.onerror = function() {
          throw new Error('Upload failed');
        };
        
        xhr.send(formData);
      } catch (err) {
        if (isImage) {
          imageUploadStatus.value = `Error: ${err.message}`;
          isImageUploading.value = false;
        } else {
          uploadStatus.value = `Error: ${err.message}`;
          isUploading.value = false;
        }
        error.value = `File upload failed: ${err.message}`;
      }
    };
    
    // Handle video uploaded event
    const handleVideoUploaded = (files) => {
      if (files && files.length > 0) {
        formData.video_over = files[0].url;
      }
    };
    
    // Form submission
    const handleSubmit = async () => {
      error.value = null;
      isSubmitting.value = true;
      
      try {
        // Validate required audio file for certain types
        if (['breathing', 'bedtime', 'souds_of_nature'].includes(formData.type) && 
            !audioFileId.value && !props.initialData.file_id) {
          throw new Error('Audio file is required for this content type');
        }
        
        // Prepare submission data
        const submissionData = {
          ...formData,
          file_id: audioFileId.value || formData.file_id,
          image_id: imageFileId.value || formData.image_id
        };
        
        // Emit save event with data for parent handling
        emit('save', {
          id: props.editingId,
          data: submissionData,
          isEdit: !!props.editingId
        });
        
        isSubmitting.value = false;
      } catch (err) {
        error.value = err.message;
        isSubmitting.value = false;
      }
    };
    
    return {
      formData,
      error,
      isSubmitting,
      fileInput,
      selectedFile,
      uploadStatus,
      uploadProgress,
      isUploading,
      imageInput,
      selectedImage,
      imageUploadStatus,
      imageUploadProgress,
      isImageUploading,
      triggerFileInput,
      handleFileChange,
      triggerImageInput,
      handleImageChange,
      handleSubmit,
      handleVideoUploaded,
    };
  }
};
</script>

<style scoped>
.sleep-sidebar-editor {
  display: flex;
  height: 100vh;
}

.editor-container {
  flex: 1;
  margin-left: 300px; /* Width of the sidebar */
  padding: 2rem;
  height: 100%;
  overflow-y: auto;
}

.editor-header {
  display: flex;
  align-items: center;
  margin-bottom: 2rem;
}

.back-button {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: none;
  border: none;
  color: var(--primary-color, #4f46e5);
  font-size: 1rem;
  cursor: pointer;
  padding: 0.5rem 0.75rem;
  border-radius: 8px;
  margin-right: 1rem;
  transition: all 0.2s ease;
}

.back-button:hover {
  background-color: rgba(79, 70, 229, 0.1);
}

.editor-header h2 {
  font-size: 1.5rem;
  font-weight: 600;
  margin: 0;
  color: var(--heading-color, #111827);
}

.editor-form {
  max-width: 800px;
}

.form-group {
  margin-bottom: 1.5rem;
}

.form-group label {
  display: block;
  font-weight: 500;
  margin-bottom: 0.5rem;
  color: var(--label-color, #374151);
}

.form-group input[type="text"],
.form-group input[type="number"],
.form-group select,
.form-group textarea {
  width: 100%;
  padding: 0.75rem;
  border: 1px solid var(--border-color, #d1d5db);
  border-radius: 8px;
  font-size: 1rem;
  transition: border 0.2s ease;
  background-color: var(--input-bg, #fff);
  color: var(--text-color, #111827);
}

.form-group input[type="text"]:focus,
.form-group input[type="number"]:focus,
.form-group select:focus,
.form-group textarea:focus {
  border-color: var(--primary-color, #4f46e5);
  outline: none;
  box-shadow: 0 0 0 2px rgba(79, 70, 229, 0.1);
}

.file-upload-container {
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.file-input {
  display: none;
}

.file-select-button {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1rem;
  border: 1px dashed var(--border-color, #d1d5db);
  border-radius: 8px;
  background-color: var(--input-bg, #fff);
  color: var(--text-secondary, #6b7280);
  cursor: pointer;
  font-size: 0.95rem;
  transition: all 0.2s ease;
}

.file-select-button:hover {
  border-color: var(--primary-color, #4f46e5);
  color: var(--primary-color, #4f46e5);
}

.selected-file {
  font-size: 0.9rem;
  color: var(--text-secondary, #6b7280);
  margin-top: 0.25rem;
}

.upload-status {
  margin-top: 0.5rem;
  font-size: 0.85rem;
  color: var(--text-color, #111827);
  position: relative;
  height: 22px;
  display: flex;
  align-items: center;
  padding-left: 0.25rem;
  border-radius: 4px;
  overflow: hidden;
}

.upload-status.uploading {
  background-color: rgba(79, 70, 229, 0.05);
}

.upload-progress {
  position: absolute;
  left: 0;
  top: 0;
  height: 100%;
  background-color: rgba(79, 70, 229, 0.15);
  z-index: 1;
  transition: width 0.3s ease;
}

.upload-status span {
  position: relative;
  z-index: 2;
}

.status-toggle {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.status-toggle label {
  margin-bottom: 0;
}

.toggle-container {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.toggle {
  position: relative;
  display: inline-block;
  width: 50px;
  height: 26px;
}

.toggle input {
  opacity: 0;
  width: 0;
  height: 0;
}

.slider {
  position: absolute;
  cursor: pointer;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: var(--toggle-bg, #e5e7eb);
  transition: .4s;
  border-radius: 34px;
}

.slider:before {
  position: absolute;
  content: "";
  height: 18px;
  width: 18px;
  left: 4px;
  bottom: 4px;
  background-color: white;
  transition: .4s;
  border-radius: 50%;
}

input:checked + .slider {
  background-color: var(--primary-color, #4f46e5);
}

input:focus + .slider {
  box-shadow: 0 0 1px var(--primary-color, #4f46e5);
}

input:checked + .slider:before {
  transform: translateX(24px);
}

.status-label {
  font-size: 0.9rem;
  color: var(--text-secondary, #6b7280);
}

.form-error {
  background-color: rgba(239, 68, 68, 0.1);
  color: #ef4444;
  padding: 0.75rem;
  border-radius: 8px;
  font-size: 0.9rem;
  margin-bottom: 1.5rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.form-actions {
  display: flex;
  gap: 1rem;
  margin-top: 2rem;
}

.btn-primary,
.btn-secondary {
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  font-size: 1rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  transition: all 0.2s ease;
  min-width: 120px;
}

.btn-primary {
  background-color: var(--primary-color, #4f46e5);
  color: white;
  border: none;
}

.btn-primary:hover:not(:disabled) {
  background-color: #4338ca;
}

.btn-primary:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.btn-secondary {
  background-color: transparent;
  border: 1px solid var(--border-color, #d1d5db);
  color: var(--text-color, #111827);
}

.btn-secondary:hover {
  background-color: rgba(0, 0, 0, 0.05);
}

.spinner {
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-radius: 50%;
  border-top: 2px solid white;
  width: 16px;
  height: 16px;
  animation: spin 1s linear infinite;
}

.spinner.small {
  width: 14px;
  height: 14px;
  border-width: 2px;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

/* Responsive styles */
@media (max-width: 768px) {
  .editor-container {
    margin-left: 0; /* Remove sidebar space on mobile */
    padding: 1rem;
  }
}

/* Dark theme overrides */
:root[data-theme="dark"] .editor-container {
  background-color: #0f172a;
}

:root[data-theme="dark"] .editor-header h2 {
  color: #f9fafb;
}

:root[data-theme="dark"] .form-group label {
  color: #e5e7eb;
}

:root[data-theme="dark"] .form-group input[type="text"],
:root[data-theme="dark"] .form-group input[type="number"],
:root[data-theme="dark"] .form-group select,
:root[data-theme="dark"] .form-group textarea {
  background-color: #1f2937;
  color: #f9fafb;
  border-color: #374151;
}

:root[data-theme="dark"] .file-select-button {
  background-color: #1f2937;
  color: #d1d5db;
  border-color: #374151;
}

:root[data-theme="dark"] .btn-secondary {
  background-color: transparent;
  border-color: #374151;
  color: #e5e7eb;
}

.video-preview {
  width: 100%;
  border-radius: 8px;
  overflow: hidden;
  background-color: #f3f4f6;
}
</style> 